import { Component, ElementRef, inject, OnInit, Renderer2, ViewChild, viewChild } from '@angular/core';
import { ProgressBarComponent } from '../../core/progress-bar/progress-bar.component';
import { Router, RouterOutlet } from '@angular/router';
import { CommonModule } from '@angular/common';
import { LoadingComponent } from '../pages/loading/loading.component';
import ApexCharts from 'apexcharts';
import { AuthService } from '../../core/services/auth.service';
import { AuthUser } from '../../core/models/auth/auth.interface';
import { User } from '../../core/models/user.interface';
import { BaseComponent } from '../../core/shared/base.component';
import { TestsService } from '../../core/services/tests.service';
import { UsersService } from '../../core/services/users.service';
import { LoaderService } from '../../core/shared/services/loader.service';
import { RadarChartComponent } from '../../core/shared/ui/radar-chart/radar-chart.component';
import { TutorialDialogComponent } from '../pages/tutorial-dialog/tutorial-dialog.component';
import { Subject } from 'rxjs';
import { ConfigurationService } from '../../core/services/configuration.service';
import { environment } from '../../../environments/environment';
import { SUBSCRIPTION_TYPES } from '../../core/shared/constants';
import { YouTubePlayerComponent } from '../../core/shared/components/youtube-player/youtube-player.component';
import { WaitlistService } from '../../core/services/waitlist.service';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-layout-result',
  standalone: true,
  imports: [
    ProgressBarComponent,
    CommonModule,
    LoadingComponent,
    RadarChartComponent,
    TutorialDialogComponent,
    FormsModule,
    YouTubePlayerComponent
  ],
  templateUrl: './layout-result.component.html',
  styleUrl: './layout-result.component.scss'
})
export class LayoutResultComponent extends BaseComponent implements OnInit {
  private authService = inject(AuthService);
  private testService = inject(TestsService);
  private usersService = inject(UsersService);
  private loader = inject(LoaderService);
  private configService = inject(ConfigurationService);
  private waitlistService = inject(WaitlistService);

  authUser: AuthUser = this.authService.getAuthUser()!;
  // user: User = this.authService.getUser()!;
  user!: User;

  isLoading = true;
  currentStep = 3;
  series: any[] = [];
  seriesApply: any[] = [];
  seriesApplyIndex: number = -1;
  seriesApplyIndexComparative: number = -1;
  analysisAreas: any[] = [];
  categories: string[] = [];
  testHoyColor = "#00D15A";
  testFuturoColor = "#ce0024";
  colors: string[] = [this.testHoyColor, this.testFuturoColor];
  barChartInstance: any;

  // Variables para control de sonido del video
  videoIsMuted: boolean = true;
  currentVideoId: string | null = null;

  chart = viewChild<ElementRef>("chart");
  barChart = viewChild<ElementRef>("barChart");
  progressBarEspaciado = viewChild<ElementRef>("progressBarEspaciado");

  optionSelected = 0;
  summary: any;

  eventsSubject: Subject<void> = new Subject<void>();

  // Wheel of Life properties
  userAge: number = 0;
  priorityArea: string = '';
  opportunityAreas: string = '';
  strengthAreas: string = '';

  // Variable para mostrar el banner de felicitaciones por salario alto
  showSalaryBanner: boolean = false;
  isHighSalaryUser: boolean = false;

  // Banner de Lista de espera
  showWaitlistModal: boolean = false;
  isSubmittingWaitlist: boolean = false;
  waitlistForm = {
    nombre: '',
    apellido: '',
    telefono: '',
    correoElectronico: '',
    provincia: '',
    distrito: ''
  };

  // Provincias y distritos para el formulario de lista de espera
  provincias = [
    'Amazonas', 'Ancash', 'Apurímac', 'Arequipa', 'Ayacucho', 'Cajamarca',
    'Callao', 'Cusco', 'Huancavelica', 'Huánuco', 'Ica', 'Junín', 'La Libertad',
    'Lambayeque', 'Lima', 'Loreto', 'Madre de Dios', 'Moquegua', 'Pasco',
    'Piura', 'Puno', 'San Martín', 'Tacna', 'Tumbes', 'Ucayali'
  ];

  distritos: { [key: string]: string[] } = {
    'Lima': ['Ancón', 'Ate', 'Barranco', 'Breña', 'Carabayllo', 'Chaclacayo', 'Chorrillos', 'Cieneguilla', 'Comas', 'El Agustino', 'Independencia', 'Jesús María', 'La Molina', 'La Victoria', 'Lince', 'Los Olivos', 'Lurigancho', 'Lurín', 'Magdalena del Mar', 'Miraflores', 'Pachacámac', 'Pucusana', 'Pueblo Libre', 'Puente Piedra', 'Punta Hermosa', 'Punta Negra', 'Rímac', 'San Bartolo', 'San Borja', 'San Isidro', 'San Juan de Lurigancho', 'San Juan de Miraflores', 'San Luis', 'San Martín de Porres', 'San Miguel', 'Santa Anita', 'Santa María del Mar', 'Santa Rosa', 'Santiago de Surco', 'Surquillo', 'Villa El Salvador', 'Villa María del Triunfo'],
    'Arequipa': ['Arequipa', 'Alto Selva Alegre', 'Cayma', 'Cerro Colorado', 'Characato', 'Chiguata', 'Jacobo Hunter', 'La Joya', 'Mariano Melgar', 'Miraflores', 'Mollebaya', 'Paucarpata', 'Pocsi', 'Polobaya', 'Quequeña', 'Sabandía', 'Sachaca', 'San Juan de Siguas', 'San Juan de Tarucani', 'Santa Isabel de Siguas', 'Santa Rita de Siguas', 'Socabaya', 'Tiabaya', 'Uchumayo', 'Vitor', 'Yanahuara', 'Yarabamba', 'Yura'],
    'Cusco': ['Cusco', 'Ccorca', 'Poroy', 'San Jerónimo', 'San Sebastian', 'Santiago', 'Saylla', 'Wanchaq'],
    'Piura': ['Piura', 'Castilla', 'Catacaos', 'Cura Mori', 'El Tallan', 'La Arena', 'La Unión', 'Las Lomas', 'Tambo Grande', 'Veintiséis de Octubre'],
    'La Libertad': ['Trujillo', 'El Porvenir', 'Florencia de Mora', 'Huanchaco', 'La Esperanza', 'Laredo', 'Moche', 'Poroto', 'Salaverry', 'Simbal', 'Victor Larco Herrera'],
    'Lambayeque': ['Chiclayo', 'Chongoyape', 'Eten', 'Eten Puerto', 'José Leonardo Ortiz', 'La Victoria', 'Lagunas', 'Monsefú', 'Nueva Arica', 'Oyotún', 'Picsi', 'Pimentel', 'Reque', 'San José', 'Santa Rosa', 'Saña', 'Cayalti', 'Pátapo', 'Pomalca', 'Pucalá', 'Tuman'],
    'Junín': ['Huancayo', 'Chacapampa', 'Chanchamayo', 'Chicche', 'Chilca', 'Chongos Alto', 'Chupaca', 'Colca', 'Cullhuas', 'El Tambo', 'Huacrapuquio', 'Hualhuas', 'Huancan', 'Huasicancha', 'Huayucachi', 'Ingenio', 'Pariahuanca', 'Pilcomayo', 'Pucara', 'Quichuay', 'Quilcas', 'San Agustín', 'San Jerónimo de Tunan', 'Saño', 'Sapallanga', 'Sicaya', 'Santo Domingo de Acobamba', 'Viques'],
    'Callao': ['Callao', 'Bellavista', 'Carmen de la Legua Reynoso', 'La Perla', 'La Punta', 'Ventanilla', 'Mi Perú'],
    'Ancash': ['Huaraz', 'Cochabamba', 'Colcabamba', 'Huanchay', 'Independencia', 'Jangas', 'La Libertad', 'Olleros', 'Pampas Grande', 'Pariacoto', 'Pira', 'Tarica'],
    'Ica': ['Ica', 'La Tinguiña', 'Los Aquijes', 'Ocucaje', 'Pachacutec', 'Parcona', 'Pueblo Nuevo', 'Salas', 'San José de Los Molinos', 'San Juan Bautista', 'Santiago', 'Subtanjalla', 'Tate', 'Yauca del Rosario'],
    'Tacna': ['Tacna', 'Alto de la Alianza', 'Calana', 'Ciudad Nueva', 'Inclan', 'Pachia', 'Palca', 'Pocollay', 'Sama', 'Coronel Gregorio Albarracín Lanchipa'],
    'Puno': ['Puno', 'Acora', 'Amantani', 'Atuncolla', 'Capachica', 'Chucuito', 'Coata', 'Huata', 'Mañazo', 'Paucarcolla', 'Pichacani', 'Plateria', 'San Antonio', 'Tiquillaca', 'Vilque'],
    'Cajamarca': ['Cajamarca', 'Asunción', 'Cospan', 'Encañada', 'Jesús', 'Llacanora', 'Los Baños del Inca', 'Magdalena', 'Matara', 'Namora', 'San Juan'],
    'Ayacucho': ['Huamanga', 'Acos Vinchos', 'Andrés Avelino Cáceres Dorregaray', 'Ayacucho', 'Carmen Alto', 'Chiara', 'Ocros', 'Pacaycasa', 'Quinua', 'San José de Ticllas', 'San Juan Bautista', 'Santiago de Pischa', 'Socos', 'Tambillo', 'Vinchos'],
    'Huancavelica': ['Huancavelica', 'Acobambilla', 'Acoria', 'Conayca', 'Cuenca', 'Huachocolpa', 'Huayllahuara', 'Laria', 'Manta', 'Mariscal Cáceres', 'Moya', 'Nuevo Occoro', 'Palca', 'Pilchaca', 'Vilca', 'Yauli'],
    'Huánuco': ['Huánuco', 'Amarilis', 'Chinchao', 'Churubamba', 'Margos', 'Quisqui', 'San Francisco de Cayran', 'San Pedro de Chaulan', 'Santa María del Valle', 'Yarumayo', 'Pillco Marca', 'Yacus'],
    'Loreto': ['Maynas', 'Alto Nanay', 'Fernando Lores', 'Indiana', 'Las Amazonas', 'Mazán', 'Napo', 'Punchana', 'Putumayo', 'Torres Causana', 'Belén', 'San Juan Bautista', 'Teniente Manuel Clavero'],
    'Ucayali': ['Coronel Portillo', 'Callería', 'Campoverde', 'Iparía', 'Masisea', 'Yarinacocha', 'Nueva Requena'],
    'San Martín': ['Moyobamba', 'Calzada', 'Habana', 'Jepelacio', 'Soritor', 'Yantalo'],
    'Madre de Dios': ['Tambopata', 'Inambari', 'Las Piedras', 'Laberinto'],
    'Moquegua': ['Moquegua', 'Carumas', 'Cuchumbaya', 'Omate', 'Quinistaquillas', 'Samegua'],
    'Pasco': ['Cerro de Pasco', 'Chaupimarca', 'Huachon', 'Huariaca', 'Huayllay', 'Ninacaca', 'Pallanchacra', 'Paucartambo', 'San Francisco de Asis de Yarusyacan', 'Simon Bolivar', 'Ticlacayan', 'Tinyahuarco', 'Vicco', 'Yanacancha'],
    'Apurímac': ['Abancay', 'Chacoche', 'Circa', 'Curahuasi', 'Huanipaca', 'Lambrama', 'Pichirhua', 'San Juan de Chacña', 'Tamburco'],
    'Amazonas': ['Chachapoyas', 'Asunción', 'Balsas', 'Cheto', 'Chiliquin', 'Chuquibamba', 'Granada', 'Huancas', 'La Jalca', 'Leimebamba', 'Levanto', 'Magdalena', 'Mariscal Castilla', 'Molinopampa', 'Montevideo', 'Olleros', 'Quinjalca', 'San Francisco de Daguas', 'San Isidro de Maino', 'Soloco', 'Sonche'],
    'Tumbes': ['Tumbes', 'Corrales', 'La Cruz', 'Pampas de Hospital', 'San Jacinto', 'San Juan de la Virgen', 'Zorritos']
  };

  // Navigation control
  private navigationChecked = false;

  // Life Areas properties
  lifeAreas = [
    {
      name: 'Salud y bienestar',
      icon: 'assets/icons/saludybienestar.png',
      expanded: false,
      description: 'Basándote en tu análisis, tu salud y bienestar están en un buen nivel. Considera mantener hábitos saludables y explorar nuevas actividades que mejoren tu bienestar físico y mental.',
      gapClass: '',
      gapClass2: ''
    },
    {
      name: 'Diversión',
      icon: 'assets/icons/diversion.png',
      expanded: false,
      description: 'Tu área de diversión muestra oportunidades de mejora. Busca actividades que realmente disfrutes y te ayuden a desconectar del estrés diario.',
      gapClass: '',
      gapClass2: ''
    },
    {
      name: 'Crecimiento personal',
      icon: 'assets/icons/crecimientopersonal.png',
      expanded: false,
      description: 'El crecimiento personal es clave para tu desarrollo. Identifica áreas específicas donde quieras mejorar y establece metas claras y alcanzables.',
      gapClass: '',
      gapClass2: ''
    },
    {
      name: 'Círculos sociales',
      icon: 'assets/icons/circulosocial.png',
      expanded: false,
      description: 'Tus círculos sociales pueden fortalecerse. Busca conectar con personas que compartan tus intereses y valores.',
      gapClass: '',
      gapClass2: ''
    },
    {
      name: 'Familia',
      icon: 'assets/icons/familia.png',
      expanded: false,
      description: 'La familia es fundamental. Dedica tiempo de calidad con tus seres queridos y fortalece estos vínculos importantes.',
      gapClass: '',
      gapClass2: ''
    },
    {
      name: 'Transformación',
      icon: 'assets/icons/transformacion.png',
      expanded: false,
      description: 'La transformación requiere tiempo y paciencia. Identifica qué cambios quieres hacer y trabaja en ellos paso a paso.',
      gapClass: '',
      gapClass2: ''
    },
    {
      name: 'Trabajo y desarrollo profesional',
      icon: 'assets/icons/trabajoydesarrollopersonal.png',
      expanded: false,
      description: 'Tu desarrollo profesional está en buen camino. Continúa aprendiendo y buscando oportunidades de crecimiento en tu carrera.',
      gapClass: '',
      gapClass2: ''
    },
    {
      name: 'Ingresos económicos',
      icon: 'assets/icons/ingreso.png',
      expanded: false,
      description: 'Los ingresos económicos requieren atención. Considera diversificar tus fuentes de ingreso y mejorar tus habilidades financieras.',
      gapClass: '',
      gapClass2: ''
    }
  ];

  // Tutorial properties
  isShowTutorial: boolean = false;
  tutorialSteps = [
    { title: 'Resultados', description: 'Visualiza tus resultados comparando el presente y futuro de tu planificación financiera.' },
    { title: 'Comparación', description: 'Selecciona entre diferentes vistas para analizar tus evaluaciones del presente y futuro.' },
    { title: 'Plan de vida', description: 'Crea tu plan de vida personalizado basado en los resultados de tu autodiagnóstico.' },
  ];
  tutorialStep: number = 1;
  tutorialTitle!: string;
  tutorialDescription!: string;
  dialogPosition = { top: '0px', left: '0px' };
  dialogWidth = 350;
  dialogHeight = 280;

  private navigationSubscription: any;

  // ViewChild para el reproductor de YouTube
  @ViewChild('youtubePlayer', { static: false }) youtubePlayer!: ElementRef;

  constructor(private router: Router, private renderer: Renderer2) {
    super();
  }

  ngOnDestroy(): void {
    if (this.navigationSubscription) {
      this.navigationSubscription.unsubscribe();
    }
  }
  async ngOnInit(): Promise<void> {
    // Evitar múltiples inicializaciones
    if (this.navigationChecked) {
      return;
    }

    await this.verifyNavigation();
  }

  /**
   * Cierra la sesión del usuario y redirige al login
   */
  async logout(): Promise<void> {
    try {
      await this.authService.signOutApply();
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    }
  }

  async verifyNavigation() {
    // Evitar verificación múltiple
    if (this.navigationChecked) {
      return;
    }

    this.navigationChecked = true;

    this.navigationSubscription = this.authService.findUserById(this.authUser.id).subscribe({
      next: (res: any) => {
        this.user = res.data;
        // Remover la redirección automática al dashboard
        // Permitir que el usuario vea los resultados independientemente del estado de onboarding

        // Mostrar el modal con video al acceder a la página de resultados
        setTimeout(() => { this.showResultsVideo() }, 100);
      },
      error: (error) => {
        console.error('Error verificando navegación:', error);
        this.navigationChecked = false; // Reset en caso de error
      }
    });
  }

  // Obtener ID del video de Phoenix Professional para el reproductor de YouTube
  getProfessionalVideoId(): string {
    return YouTubePlayerComponent.extractVideoId(this.getProfessionalVideoUrl()) || '';
  }

  // Obtener URL del video de Phoenix Professional
  getProfessionalVideoUrl(): string {
    return environment.urlVideoProfessional;
  }

  // Obtener parámetros del video para el reproductor de YouTube
  getVideoParams(): string {
    return 'autoplay=0&mute=0&enablejsapi=1&origin=' + window.location.origin + '&rel=0&showinfo=0';
  }

  // Enviar formulario de lista de espera
  async submitWaitlistForm() {
    if (!this.isWaitlistFormValid()) {
      this.alert('Por favor, completa todos los campos requeridos.');
      return;
    }

    this.isSubmittingWaitlist = true;
    this.loader.show();

    try {
      // Preparar datos para enviar
      const waitlistData = {
        ...this.waitlistForm,
        userId: this.authUser?.id || undefined,
        createdAt: new Date().toISOString()
      };

      // Llamar al servicio de waitlist
      await this.waitlistService.registerToWaitlist(waitlistData).toPromise();

      this.closeWaitlistModal();
      this.alert('¡Gracias! Te has registrado exitosamente en nuestra lista de espera. Te contactaremos pronto.');
    } catch (error) {
      console.error('Error al registrar en lista de espera:', error);
      this.alert('Hubo un error al registrar tu solicitud. Por favor, intenta nuevamente.');
    } finally {
      this.isSubmittingWaitlist = false;
      this.loader.hide();
    }
  }

  // Validar formulario de lista de espera
  isWaitlistFormValid(): boolean {
    return !!(
      this.waitlistForm.nombre?.trim() &&
      this.waitlistForm.apellido?.trim() &&
      this.waitlistForm.telefono?.trim() &&
      this.waitlistForm.correoElectronico?.trim() &&
      this.waitlistForm.provincia?.trim() &&
      this.waitlistForm.distrito?.trim()
    );
  }

  // Obtener distritos por provincia
  getDistritosByProvincia(): string[] {
    if (this.waitlistForm.provincia && this.distritos[this.waitlistForm.provincia]) {
      return this.distritos[this.waitlistForm.provincia];
    }
    return [];
  }

  // Limpiar distrito cuando cambia la provincia
  onProvinciaChange() {
    this.waitlistForm.distrito = '';
  }

  // Cerrar modal de lista de espera
  closeWaitlistModal() {
    this.showWaitlistModal = false;
    this.resetWaitlistForm();
  }

  // Resetear formulario de lista de espera
  resetWaitlistForm() {
    this.waitlistForm = {
      nombre: '',
      apellido: '',
      telefono: '',
      correoElectronico: '',
      provincia: '',
      distrito: ''
    };
  }

  // Abrir modal de lista de espera
  openWaitlistModal() {
    // Pre-llenar algunos campos con información del usuario si está disponible
    if (this.user) {
      this.waitlistForm.nombre = this.user.firstName || '';
      this.waitlistForm.apellido = this.user.lastName || '';
      this.waitlistForm.correoElectronico = this.user.email || '';
      this.waitlistForm.telefono = this.user.phone || '';
    }
    this.showWaitlistModal = true;
  }

  /**
   * Muestra el modal con video para la página de resultados
   */
  private showResultsVideo(): void {
    this.loader.show();
    this.configService.getEmbeddedVideos().subscribe({
      next: (response: any) => {
        this.loader.hide();
        if (response?.data && response.data.length > 0) {
          const videoData = response.data.find((video: any) => video.type === 'video_modal_resultado_de_autodiagnostico');

          if (videoData && videoData.url) {
            // Obtener el ID del video usando el método de la clase base
            const videoId = this.getYoutubeVideoId(videoData.url);
            if (videoId) {
              this.currentVideoId = videoId;
              this.showVideoModal(videoId);
            } else {
              this.showDefaultResultsModal();
            }
          } else {
            // Si no hay video, mostrar el modal con texto
            this.showDefaultResultsModal();
          }
        } else {
          // Si no hay videos configurados, mostrar el modal con texto
          this.showDefaultResultsModal();
        }
      },
      error: (error: any) => {
        console.error('Error al obtener videos embebidos:', error);
        this.loader.hide();
        // En caso de error, mostrar el modal con texto
        this.showDefaultResultsModal();
      }
    });
  }

  /**
   * Muestra el modal con el video y controles de sonido
   */
  showVideoModal(videoId: string): void {
    // Crear estilos para el video y el botón de sonido
    const styles = `
      <style>
        .results-video-container {
          width: 100%;
          display: flex;
          justify-content: center;
        }
        .results-video-wrapper {
          width: 350px;
          height: 350px;
          position: relative;
          overflow: hidden;
          background-color: black;
          border-radius: 0;
        }
        .results-video-iframe {
          position: absolute;
          top: 0%;
          left: 50%;
          width: 100%;
          height: 100%;
          transform: translateX(-50%);
          border: none;
          pointer-events: none;
          object-fit: cover;
        }
        .sound-toggle-btn {
          position: absolute;
          bottom: 55px;
          right: 10px;
          background-color: rgba(0, 0, 0, 0.7);
          color: white;
          border: none;
          border-radius: 50%;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          z-index: 100;
          transition: all 0.2s ease;
        }
        .sound-toggle-btn:hover {
          background-color: rgba(0, 0, 0, 0.9);
          transform: scale(1.1);
        }
        .fullscreen-btn {
          position: absolute;
          bottom: 10px;
          right: 10px;
          background: rgba(0,0,0,0.6);
          border: none;
          border-radius: 50%;
          padding: 8px;
          cursor: pointer;
        }
      </style>
    `;

    // Crear URL del video con el parámetro mute
    const videoSrc = `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&mute=1&controls=0&loop=1&playlist=${videoId}&rel=0&showinfo=0&modestbranding=1&disablekb=1&fs=0&iv_load_policy=3&cc_load_policy=0&enablejsapi=0&origin=${window.location.origin}&hl=en&cc=0&playsinline=1&autohide=1&color=white&start=1&theme=dark&vq=hd1080&showsearch=0&ecver=2&playerapiid=ytplayer&wmode=transparent&annotation=0`;

    // Crear HTML del video con botón de sonido
    const videoHtml = `
      ${styles}
      <div class="results-video-container">
        <div class="results-video-wrapper">
          <iframe 
            id="modal-video-frame"
            class="results-video-iframe"
            src="${videoSrc}" 
            frameborder="0" 
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
            allowfullscreen>
          </iframe>
          <button 
            id="video-sound-toggle" 
            class="sound-toggle-btn" 
            title="Activar sonido">
            <!-- Icono de silencio -->
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
              <path d="M3,9v6h4l5,5V4L7,9H3z" fill="white"/>
              <path d="M16.5,12c0-1.77-1.02-3.29-2.5-4.03v8.05C15.48,15.29,16.5,13.77,16.5,12z M14,3.23v2.06 c2.89,0.86,5,3.54,5,6.71s-2.11,5.85-5,6.71v2.06c4.01-0.91,7-4.49,7-8.77S18.01,4.14,14,3.23z" fill="white" opacity="0.5"/>
              <line x1="23" y1="3" x2="3" y2="21" stroke="white" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </button>
          <button id="video-fullscreen-toggle" class="fullscreen-btn" title="Pantalla completa">
            <!-- Icono pantalla completa -->
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="white">
              <path d="M7 14H5v5h5v-2H7v-3zm0-4h2V7h3V5H7v5zm10 4h2v3h-3v2h5v-5h-4zm-3-9v2h3v3h2V5h-5z"/>
            </svg>
          </button>
        </div>
      </div>
    `;

    // Mostrar el modal con video
    // this.info(videoHtml,
    //   "Resultados de tu autodiagnóstico",
    //   "Ver Resultados",
    //   false);

    // Agregar el evento al botón de sonido después de que el modal se haya mostrado
    setTimeout(() => {
      const soundToggleBtn = document.getElementById('video-sound-toggle');
      if (soundToggleBtn) {
        soundToggleBtn.addEventListener('click', () => this.toggleVideoSound());
      }
      const fullscreenToggleBtn = document.getElementById('video-fullscreen-toggle');
      if (fullscreenToggleBtn) {
        fullscreenToggleBtn.addEventListener('click', () => this.toggleVideoFulscreen());
      }
    }, 100);
  }

  /**
   * Activa o desactiva el sonido del video
   */
  toggleVideoSound(): void {
    this.videoIsMuted = !this.videoIsMuted;

    if (!this.currentVideoId) return;

    const videoFrame = document.getElementById('modal-video-frame') as HTMLIFrameElement;
    const soundToggleBtn = document.getElementById('video-sound-toggle');

    if (videoFrame) {
      // Crear nueva URL con el parámetro mute actualizado
      const newSrc = `https://www.youtube-nocookie.com/embed/${this.currentVideoId}?autoplay=1&mute=${this.videoIsMuted ? '1' : '0'}&controls=0&loop=1&playlist=${this.currentVideoId}&rel=0&showinfo=0&modestbranding=1&disablekb=1&fs=0&iv_load_policy=3&cc_load_policy=0&enablejsapi=0&origin=${window.location.origin}&hl=en&cc=0&playsinline=1&autohide=1&color=white&start=1&theme=dark&vq=hd1080&showsearch=0&ecver=2&playerapiid=ytplayer&wmode=transparent&annotation=0`;

      // Actualizar el iframe con la nueva URL
      videoFrame.src = newSrc;
    }

    // Actualizar el botón con el icono correspondiente
    if (soundToggleBtn) {
      soundToggleBtn.innerHTML = this.videoIsMuted ?
        // Icono de silencio
        `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
          <path d="M3,9v6h4l5,5V4L7,9H3z" fill="white"/>
          <path d="M16.5,12c0-1.77-1.02-3.29-2.5-4.03v8.05C15.48,15.29,16.5,13.77,16.5,12z M14,3.23v2.06 c2.89,0.86,5,3.54,5,6.71s-2.11,5.85-5,6.71v2.06c4.01-0.91,7-4.49,7-8.77S18.01,4.14,14,3.23z" fill="white" opacity="0.5"/>
          <line x1="23" y1="3" x2="3" y2="21" stroke="white" stroke-width="2" stroke-linecap="round"/>
        </svg>` :
        // Icono de sonido activo
        `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
          <path d="M3,9v6h4l5,5V4L7,9H3z" fill="white"/>
          <path d="M16.5,12c0-1.77-1.02-3.29-2.5-4.03v8.05C15.48,15.29,16.5,13.77,16.5,12z M14,3.23v2.06 c2.89,0.86,5,3.54,5,6.71s-2.11,5.85-5,6.71v2.06c4.01-0.91,7-4.49,7-8.77S18.01,4.14,14,3.23z" fill="white"/>
        </svg>`;

      soundToggleBtn.setAttribute('title', this.videoIsMuted ? 'Activar sonido' : 'Silenciar');
    }
  }
  toggleVideoFulscreen(): void {
    const videoFrame = document.getElementById('modal-video-frame') as HTMLIFrameElement;
    const wrapper = videoFrame?.parentElement;

    if (document.fullscreenElement) {
      document.exitFullscreen();
      return;
    }
    if (wrapper?.requestFullscreen) {
      wrapper.requestFullscreen();
    } else if ((wrapper as any).webkitRequestFullscreen) {
      (wrapper as any).webkitRequestFullscreen();
    } else if ((wrapper as any).mozRequestFullScreen) {
      (wrapper as any).mozRequestFullScreen();
    } else if ((wrapper as any).msRequestFullscreen) {
      (wrapper as any).msRequestFullscreen();
    } else {
      console.warn('Fullscreen API no soportada');
    }
  }

  /**
   * Muestra el modal de resultados por defecto (con texto)
   */
  private showDefaultResultsModal(): void {
    this.info(`<p style="font-size: 1rem; text-align: start;">Estos son los resultados de tu autodiagnóstico. 
      Aquí podrás visualizar tu nivel de satisfacción actual (naranja) y tu nivel deseado (rojo) para cada dimensión de tu vida.<br><br>
      Observa las brechas entre tu situación actual y la deseada para identificar las áreas donde deseas enfocar tu desarrollo personal.</p>`,
      "Resultados de tu autodiagnóstico",
      "Continuar",
      false);
  }

  onLoadingComplete() {
    this.isLoading = false;
    this.getTests();
    this.checkSuscriptionPublicityToShow();
  }

  async createBarChart() {
    // Verificar que el elemento del DOM existe
    const chartElement = this.barChart()?.nativeElement;
    if (!chartElement) {
      console.warn('Elemento del gráfico no encontrado, esperando a que esté disponible...');
      // Esperar un poco y reintentar
      setTimeout(() => {
        this.createBarChart();
      }, 100);
      return;
    }

    var options = {
      series: this.seriesApply,
      chart: {
        type: 'bar',
        height: 430,
        toolbar: {
          show: false,
        },
      },
      colors: ['#00D15A', '#ce0024'],
      plotOptions: {
        bar: {
          horizontal: true,
          dataLabels: {
            position: 'top',
          },
        }
      },
      dataLabels: {
        enabled: true,
        offsetX: -6,
        style: {
          fontSize: '12px',
          colors: ['#fff']
        }
      },
      stroke: {
        show: true,
        width: 1,
        colors: ['#fff']
      },
      tooltip: {
        shared: true,
        intersect: false
      },
      xaxis: {
        categories: this.categories,
        max: 10,
        labels: {
          style: {
            fontSize: '12px',
            whiteSpace: 'normal',
          },
        }
      },
      legend: {
        show: false
      },
    };

    if (this.barChartInstance) {
      await this.barChartInstance.destroy();
    }

    // Crea una nueva instancia del gráfico
    this.barChartInstance = new ApexCharts(chartElement, options);
    this.barChartInstance.render();
  }

  showOnlyFirstColor() {
    const colors = ['#00D15A'];
    this.updateBarChart(colors);
  }

  showOnlySecondColor() {
    const colors = ['#ce0024'];
    this.updateBarChart(colors);
  }

  showBothColors() {
    const colors = ['#00D15A', '#ce0024'];
    this.updateBarChart(colors);
  }

  updateBarChart(colors: string[]) {
    if (this.barChartInstance) {
      this.barChartInstance.updateSeries(this.seriesApply, colors);
    }
  }

  isExecutiveUser(): boolean {
    return !!(
      this.user &&
      this.user.type &&
      this.user.type.id === 1 && // Customer
      this.user.type.suscriptionType &&
      this.user.type.suscriptionType.id === SUBSCRIPTION_TYPES.Executive.id.toString()
    );
  }

  // Verificar elegibilidad para Executive basado en rango salarial
  checkSuscriptionPublicityToShow() {
    console.log('checkSuscriptionPublicityToShow: ejecutándose...');
    console.log('checkSuscriptionPublicityToShow: user is ', this.user);

    if (!this.user) {
      console.log('checkSuscriptionPublicityToShow: No hay usuario');
      return;
    }

    if (this.user.type && this.user.type.id != 1) {
      console.log('checkSuscriptionPublicityToShow: Usuario no es Cliente');
      return;
    }

    if (this.user.type.suscriptionType && this.user.type.suscriptionType.id != "1") {
      console.log('checkSuscriptionPublicityToShow: Usuario ya esta suscrito ', this.user.type);
      return;
    }

    // Show the Suscribe banner (Professional by default)
    this.showSalaryBanner = true;

    const questionSalary = this.user.profile.registerQuestions.find(q =>
      q.title?.includes("¿En qué rango aproximado están tus ingresos  mensuales? Tu respuesta es confidencial y solo se usa para personalizar tu experiencia.")
    );

    if (questionSalary.value === "5001-más USD") {
      console.log('Ready for Executive');
      // Make the banner Executive
      this.isHighSalaryUser = true;
    }

  }

  bannerClickExecutive() {
    console.log("bannerClickExecutive");
    const userName = this.user?.firstName || this.user?.name || 'Usuario';
    const message = environment.whatsapp.executiveMessage.replace('{userName}', userName);
    const phoneNumber = environment.whatsapp.phoneNumber;
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;

    // Abrir WhatsApp en nueva ventana
    window.open(whatsappUrl, '_blank');
  }

  bannerClickProfessional() {
    console.log("bannerClickProfessional");
    this.openWaitlistModal();
  }

  getTests(): void {
    this.testService.find(this.authUser.id).subscribe({
      next: (res: any) => {
        console.log('getTests: res', res);
        if (res.data !== null) {
          let tests = res.data.filter((x: any) => x.type.id == 2 || x.type.id == 3);

          tests = tests.sort((a: any, b: any) => a.type.id - b.type.id);

          tests.forEach((item: any) => {
            item.questions = item.questions.slice().sort((a: any, b: any) => a.dimension.name.localeCompare(b.dimension.name));
          });

          this.series = tests.map((x: any) => ({
            name: x.type.id === 2 ? 'Donde estás' : 'Donde quieres estar',
            data: x.questions.map((q: any) => q.answer && q.answer.value ? parseInt(q.answer.value) : 0),
            dimensions: x.questions.map((q: any) => q.dimension),
            hidden: false
          }));

          this.categories = tests.find((x: any) => x.type.id == 2).questions.map((q: any) => q.dimension.name);

          this.seriesApply = this.series;

          // if we have series, we show spider-chart buttons
          if (this.seriesApply.length > 0) this.changeSeriesToShow(0);

          // Generar analysisAreas dinámicamente
          this.generateAnalysisAreas(tests);

          // Solo crear el gráfico si el elemento existe
          if (this.barChart()?.nativeElement) {
            this.createBarChart();
          } else {
            console.warn('Elemento barChart no encontrado, omitiendo creación del gráfico');
          }

        }
      },
      error: (err: any) => {
        console.log('err', err);
      }
    });
  }

  changeSeriesToShow(id: number) {
    //return if we out of bounds
    if (id != this.seriesApplyIndexComparative && id > this.seriesApply.length) return;

    this.seriesApplyIndex = id;

    // turn off all showing data
    this.seriesApply.forEach(series => {
      series.hidden = true;
    });

    // we apply comparative -> turn all on
    if (this.seriesApplyIndex === this.seriesApplyIndexComparative) {
      this.seriesApply.forEach(series => series.hidden = false);
      this.eventsSubject.next();
      return;
    }

    //we apply specific series
    this.seriesApply[this.seriesApplyIndex].hidden = false;
    this.eventsSubject.next();
  }

  getSeriesColor(id: number): string {
    if (id != this.seriesApplyIndexComparative && id > this.seriesApply.length) return '#ccc';
    if (id === this.seriesApplyIndexComparative) return '#000000';
    
    const colorNow = '#00D15A';
    const colorNext = '#ce0024';
    
    if (this.seriesApply[id].name === 'Donde estás') {
      return colorNow;
    } else if (this.seriesApply[id].name === 'Donde quieres estar') {
      return colorNext;
    }
    
    // Colores por defecto basados en el índice
    return id === 0 ? colorNow : colorNext;
  }

  showSummary(index: number): void {
    this.summary = {
      title: this.categories[index],
      details: this.seriesApply.map(x => ({ name: x.name, value: x.data[index], max: 10 }))
    };
  }

  async showChartLegend(option: number) {
    this.optionSelected = option;
    switch (option) {
      case 0:
        this.seriesApply.forEach(s => s.hidden = false);
        this.showBothColors();
        break;
      case 1:
        this.seriesApply[0].hidden = false;
        this.seriesApply[1].hidden = true;
        this.showOnlyFirstColor();
        break;
      case 2:
        this.seriesApply[0].hidden = true;
        this.seriesApply[1].hidden = false;
        this.showOnlySecondColor();
        break;
    }
    this.eventsSubject.next();
    // await this.createPolarAreaChart();
  }

  crearPlan() {
    // Validar si el usuario ya completó el onboarding
    if (this.user?.profile?.onboardingComplete === true) {
      console.log('El usuario ya completó el onboarding, redirigiendo directamente...');
      this.router.navigate(['/dashboard/objectives']);
      return;
    }

    this.loader.show();
    this.usersService.updateOnboardingComplete(this.authUser!!.id)
      .subscribe({
        next: async (res) => {
          if (res.data) {
            // Actualizar el estado local del usuario
            this.user.profile.onboardingComplete = true;

            await this.router.navigate(['/dashboard/objectives']);
          }
          this.loader.hide();
        },
        error: err => {
          this.errorDefault(err);
          this.loader.hide();
        }
      });

  }

  getGapClass(gap: number): string {
    const gapRanges = environment.gapRanges;
    const gapCssClasses = environment.gapCssClasses;
    const matchingRange = gapRanges.find(range => gap >= range.min && gap <= range.max);

    if (matchingRange && gapCssClasses[matchingRange.key as keyof typeof gapCssClasses]) {
      return gapCssClasses[matchingRange.key as keyof typeof gapCssClasses].small;
    }

    return 'gap-small'; // default
  }

  getGapClass2(gap: number): string {
    const gapRanges = environment.gapRanges;
    const gapCssClasses = environment.gapCssClasses;
    const matchingRange = gapRanges.find(range => gap >= range.min && gap <= range.max);

    if (matchingRange && gapCssClasses[matchingRange.key as keyof typeof gapCssClasses]) {
      return gapCssClasses[matchingRange.key as keyof typeof gapCssClasses].small2;
    }

    return 'gap-small2'; // default
  }

  // Método helper para determinar si un gap es de oportunidad usando configuración dinámica
  private isOpportunityGap(gap: number): boolean {
    const gapRanges = environment.gapRanges;
    const gapTypes = environment.gapTypes;
    const matchingRange = gapRanges.find(range => gap >= range.min && gap <= range.max);

    // Usar configuración dinámica para determinar si es oportunidad
    return matchingRange ? gapTypes.opportunity.includes(matchingRange.key) : false;
  }

  // Método helper para determinar si un gap es de fortaleza usando configuración dinámica
  private isStrengthGap(gap: number): boolean {
    const gapRanges = environment.gapRanges;
    const gapTypes = environment.gapTypes;
    const matchingRange = gapRanges.find(range => gap >= range.min && gap <= range.max);

    // Usar configuración dinámica para determinar si es fortaleza y que sea mayor a 0
    return matchingRange ? gapTypes.strength.includes(matchingRange.key) && gap > 0 : false;
  }

  generateAnalysisAreas(tests: any[]): void {
    // Obtener el test del presente (type.id == 2) y futuro (type.id == 3)
    const presentTest = tests.find((x: any) => x.type.id == 2);
    const futureTest = tests.find((x: any) => x.type.id == 3);

    if (presentTest && futureTest) {
      // Crear un mapa de dimensiones para facilitar la búsqueda
      const presentMap = new Map();
      const futureMap = new Map();

      presentTest.questions.forEach((q: any) => {
        presentMap.set(q.dimension.name, parseInt(q.answer?.value || '0'));
      });

      futureTest.questions.forEach((q: any) => {
        futureMap.set(q.dimension.name, parseInt(q.answer?.value || '0'));
      });

      // Generar analysisAreas con los datos reales
      this.analysisAreas = presentTest.questions.map((q: any) => {
        const presentValue = presentMap.get(q.dimension.name) || 0;
        const futureValue = futureMap.get(q.dimension.name) || 0;
        const gap = Math.max(futureValue - presentValue, 1);

        return {
          name: q.dimension.name.toUpperCase(),
          present: presentValue,
          future: futureValue,
          gap: gap
        };
      });

      // Actualizar el estado expanded de lifeAreas basado en los gaps
      this.updateLifeAreasExpandedState();

      // Calcular la edad del usuario basándose en las respuestas
      //console.log(JSON.stringify(this.user.profile));
      this.calculateUserAge(tests);
      this.calculatePriorityAndAreas();
      //this.priorityArea = this.user?.profile?.registerQuestions?.[2]?.value || '';

      console.log('Analysis Areas generadas:', this.analysisAreas);
    }
  }

  private calculateUserAge(tests: any[]): void {
    // Obtener las preguntas de registro del perfil del usuario
    const registerQuestions = this.user?.profile?.registerQuestions || [];

    let currentAgeRange = '';
    let retirementAgeRange = '';
    let questionsFound = 0;
    //console.log('registerQuestions', registerQuestions);
    // Buscar las preguntas específicas sobre edad en registerQuestions
    registerQuestions.forEach((question: any) => {
      const questionTitle = question.title || '';

      // Buscar pregunta sobre rango de edad actual
      if (questionTitle === '¿Cuál es el rango de tu edad?') {
        currentAgeRange = question.value;
        questionsFound++;
        console.log('Edad actual encontrada:', currentAgeRange);
      }

      // Buscar pregunta sobre edad de jubilación
      if (questionTitle === '¿A que edad consideras importante comenzar a prepararte para la etapa post laboral?') {
        retirementAgeRange = question.value;
        questionsFound++;
        console.log('Edad de jubilación encontrada:', retirementAgeRange);
      }
    });

    // Calcular los años que faltan hasta la jubilación
    if (questionsFound >= 2 && currentAgeRange && retirementAgeRange) {
      // Si ambos rangos son iguales, calcular la diferencia entre edad mínima y máxima
      if (currentAgeRange === retirementAgeRange) {
        const ageRange = this.getAgeRangeValues(currentAgeRange);
        this.userAge = ageRange.max - ageRange.min;
        console.log('Rangos iguales - Diferencia entre edad mínima y máxima:', this.userAge);
      } else {
        // Si los rangos son diferentes, usar el número mayor de cada rango
        const currentAgeMax = this.convertAgeRangeToNumber(currentAgeRange);
        const retirementAgeMax = this.convertRetirementAgeToNumber(retirementAgeRange);
        this.userAge = retirementAgeMax - currentAgeMax;
        console.log('Rangos diferentes - Años que faltan hasta jubilación:', this.userAge);
      }
    } else if (currentAgeRange) {
      // Si solo se encuentra la edad actual, usar un valor por defecto
      this.userAge = 20; // Años por defecto si no se encuentra la edad de jubilación
      console.log('Usando años por defecto (solo edad actual):', this.userAge);
    } else if (retirementAgeRange) {
      // Si solo se encuentra la edad de jubilación, usar un valor por defecto
      this.userAge = 20; // Años por defecto si no se encuentra la edad actual
      console.log('Usando años por defecto (solo edad jubilación):', this.userAge);
    } else {
      // Valor por defecto si no se encuentran las preguntas
      this.userAge = 20;
      console.log('Usando años por defecto:', this.userAge);
    }
  }

  private convertAgeRangeToNumber(ageRange: string): number {
    switch (ageRange) {
      case 'Menos de 50 años':
        return 50; // Límite superior del rango
      case '51 - 55 años':
        return 51; // Límite superior del rango
      case '56 - 60 años':
        return 56; // Límite superior del rango
      case '61 - 65 años':
        return 61; // Límite superior del rango
      case 'Más de 66 años':
        return 66; // Valor representativo para 66+
      default:
        return 0;
    }
  }

  private convertRetirementAgeToNumber(retirementAge: string): number {
    switch (retirementAge) {
      case 'Menos de 50 años':
        return 50; // Límite superior del rango
      case '51 - 55 años':
        return 51; // Límite superior del rango
      case '56 - 60 años':
        return 56; // Límite superior del rango
      case '61 - 65 años':
        return 61; // Límite superior del rango
      case 'Más de 66 años':
        return 66; // Valor representativo para 66+
      default:
        return 0;
    }
  }

  private getAgeRangeValues(ageRange: string): { min: number, max: number } {
    switch (ageRange) {
      case 'Menos de 50 años':
        return { min: 0, max: 50 };
      case '51 - 55 años':
        return { min: 51, max: 55 };
      case '56 - 60 años':
        return { min: 56, max: 60 };
      case '61 - 65 años':
        return { min: 61, max: 65 };
      case 'Más de 66 años':
        return { min: 66, max: 100 }; // Asumiendo un límite superior razonable
      default:
        return { min: 0, max: 0 };
    }
  }

  private calculatePriorityAndAreas(): void {
    // Obtener el área prioritaria del perfil del usuario
    const registerQuestions = this.user?.profile?.registerQuestions || [];
    const priorityQuestion = registerQuestions.find((q: any) =>
      q.title?.toLowerCase().includes('Pensando en tu etapa Phoenix ¿Qué área de tu vida te gustaría trabajar primero? Selecciona solo una opción.'.toLowerCase())
    );

    this.priorityArea = priorityQuestion?.value || 'Desarrollo profesional';
    console.log('Área prioritaria:', this.priorityArea);

    // Calcular áreas de oportunidad y fortalezas usando configuración dinámica
    const opportunityAreasList: string[] = [];
    const strengthAreasList: string[] = [];

    this.analysisAreas.forEach(area => {
      const areaName = this.formatAreaName(area.name);

      if (this.isOpportunityGap(area.gap)) {
        opportunityAreasList.push(areaName);
      } else if (this.isStrengthGap(area.gap)) {
        strengthAreasList.push(areaName);
      }
    });

    // Convertir arrays a strings separados por comas
    this.opportunityAreas = opportunityAreasList.join(', ');
    this.strengthAreas = strengthAreasList.join(', ');

    console.log('Áreas de oportunidad (gaps >= 4):', this.opportunityAreas);
    console.log('Áreas de fortaleza (gaps <= 3):', this.strengthAreas);
    console.log('this.user', this.user);
  }

  private formatAreaName(areaName: string): string {
    // Mapeo de nombres de áreas para mostrar en formato legible
    const areaNameMap: { [key: string]: string } = {
      'SALUD Y BIENESTAR': 'Salud y Bienestar',
      'DIVERSIÓN': 'Diversión',
      'CRECIMIENTO PERSONAL': 'Crecimiento Personal',
      'TRANSFORMACIÓN': 'Transformación',
      'CÍRCULOS SOCIALES': 'Círculos Sociales',
      'FAMILIA': 'Familia',
      'TRABAJO Y DESARROLLO PROFESIONAL': 'Trabajo y Desarrollo Profesional',
      'INGRESOS ECONÓMICOS': 'Ingresos Económicos'
    };

    return areaNameMap[areaName] || areaName;
  }

  private updateLifeAreasExpandedState(): void {
    // Mapeo de nombres de dimensiones a nombres de áreas de vida
    const dimensionToAreaMap: { [key: string]: string } = {
      'Salud y bienestar': 'Salud y bienestar',
      'Diversión': 'Diversión',
      'Crecimiento Personal': 'Crecimiento personal',
      'Transformación': 'Transformación',
      'Círculos Sociales': 'Círculos sociales',
      'Familia': 'Familia',
      'Trabajo y Desarrollo Profesional': 'Trabajo y desarrollo profesional',
      'Ingresos Económicos': 'Ingresos económicos'
    };

    // Actualizar cada área de vida
    this.lifeAreas.forEach(area => {
      // Buscar la dimensión correspondiente en analysisAreas
      const analysisArea = this.analysisAreas.find(a =>
        dimensionToAreaMap[a.name] === area.name ||
        a.name.toLowerCase() === area.name.toLowerCase()
      );

      if (analysisArea) {
        const gap = analysisArea.gap;

        // Determinar si debe estar expandida basado en el gap
        //area.expanded = gap >= 1 && gap <= 9; // Solo expandir si hay gap (1-9)

        // Actualizar la descripción basada en el rango del gap
        area.description = this.getDescriptionByGap(area.name, gap);
        area.gapClass = this.getGapClass(gap);
        area.gapClass2 = this.getGapClass2(gap);
      }
    });
  }

  private getDescriptionByGap(areaName: string, gap: number): string {
    const descriptions = environment.assessmentDescriptions;
    const gapRanges = environment.gapRanges;

    const areaDescriptions = descriptions[areaName as keyof typeof descriptions];
    if (areaDescriptions) {
      // Buscar dinámicamente el rango que corresponde al gap
      const matchingRange = gapRanges.find(range => gap >= range.min && gap <= range.max);

      if (matchingRange && areaDescriptions[matchingRange.key as keyof typeof areaDescriptions]) {
        return areaDescriptions[matchingRange.key as keyof typeof areaDescriptions];
      }
    }

    // Descripción por defecto si no hay gap o no se encuentra la área
    return 'Esta área requiere tu atención, considera revisar tus prioridades y establecer metas claras para mejorar en este aspecto de tu vida.';
  }

  toggleArea(index: number): void {
    this.lifeAreas[index].expanded = !this.lifeAreas[index].expanded;
  }

  // Método para resetear la verificación de navegación (útil para testing)
  resetNavigationCheck(): void {
    this.navigationChecked = false;
  }

  // Tutorial methods
  changeShowTutorial() {
    this.tutorialStep = 1;
    this.updateTutorialContent();
    this.isShowTutorial = !this.isShowTutorial;
    if (this.isShowTutorial === false) this.clearPreviousFocus();
  }

  resetTutorial() {
    this.tutorialStep = 1;
    this.updateTutorialContent();
  }

  nextStepTutorial() {
    if (this.tutorialStep < this.tutorialSteps.length) {
      this.tutorialStep++;
      this.updateTutorialContent();
    } else {
      this.clearPreviousFocus();
      this.isShowTutorial = false;
    }
  }

  private updateTutorialContent() {
    this.tutorialTitle = this.tutorialSteps[this.tutorialStep - 1].title;
    this.tutorialDescription = this.tutorialSteps[this.tutorialStep - 1].description;

    const targetElement = this.getElementToHighlight(this.tutorialStep);
    if (targetElement && targetElement.nativeElement) {
      this.showTutorialDialog(targetElement);
    } else {
      console.warn('No se pudo encontrar el elemento para el tutorial step:', this.tutorialStep);
    }
  }

  getElementToHighlight(option: number): ElementRef | undefined {
    switch (option) {
      case 1:
        return this.progressBarEspaciado() || this.chart();
      case 2:
        return this.barChart() || this.chart();
      case 3:
        return this.barChart() || this.chart();
      default:
        return this.chart() || this.progressBarEspaciado();
    }
  }

  showTutorialDialog(targetElement: ElementRef | undefined) {
    // Validar que el elemento existe antes de usarlo
    if (!targetElement || !targetElement.nativeElement) {
      console.warn('Elemento del tutorial no encontrado:', targetElement);
      return;
    }

    targetElement.nativeElement.scrollIntoView({
      behavior: 'smooth', // Desplazamiento suave
      block: 'center',    // Centra el elemento en la ventana
      inline: 'center'    // En caso de ser horizontal, lo centra también
    });

    const dialogPosition = this.calculateDialogPosition(targetElement, this.tutorialStep);
    this.dialogPosition = {
      top: dialogPosition.top,
      left: dialogPosition.left
    };
    this.clearPreviousFocus();
    targetElement.nativeElement.classList.add('tutorial-focus');
  }

  clearPreviousFocus() {
    document.querySelectorAll('.tutorial-focus').forEach((element) => {
      element.classList.remove('tutorial-focus');
    });
  }

  private calculateDialogPosition(targetElement: ElementRef | undefined, stepNumber: number): { top: string; left: string } {
    if (!targetElement || !targetElement.nativeElement) {
      return { top: '50px', left: '50px' };
    }

    const elementRect = targetElement.nativeElement.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const targetCenterX = elementRect.left + elementRect.width / 2;
    const targetCenterY = elementRect.top + elementRect.height / 2;
    const centerX = viewportWidth / 2;
    const centerY = viewportHeight / 2;

    let dialogLeft, dialogTop;
    const padding = 20;
    if (stepNumber > 2) {
      dialogLeft = elementRect.left + (elementRect.width - this.dialogWidth) / 2;
      dialogTop = elementRect.top - this.dialogHeight - padding;
    } else {
      dialogLeft = (targetCenterX + centerX) / 2 - this.dialogWidth / 2;
      dialogTop = (targetCenterY + centerY) / 2 - this.dialogHeight / 2;
    }

    const overlapThreshold = this.dialogHeight / 2; // Adjust this as needed
    if (dialogTop + this.dialogHeight > elementRect.top + overlapThreshold) {
      dialogTop = elementRect.top + elementRect.height + padding; // Position below the element
    }

    const left = Math.min(Math.max(dialogLeft, 0), viewportWidth - this.dialogWidth);
    const top = Math.min(Math.max(dialogTop, 0), viewportHeight - this.dialogHeight);

    return { top: `${top}px`, left: `${left}px` };
  }
}
